/**
 * Paiements (the finance console), in French, the reference language. Merged
 * into fr.ts; every other language has a payments.<lang>.ts with the same
 * keys. Money is never shown as done before the server says so: a refund
 * sent with no answer reads "statut inconnu, vérification en cours".
 */
export const paymentsFr = {
  "payments.tab.payments": "Paiements",
  "payments.tab.approvals": "Demandes d'accord",
  "payments.pick": "Choisissez un paiement dans la liste.",
  "payments.loading": "Chargement du paiement…",
  "payments.loadFailed": "Impossible de charger ce paiement.",
  "payments.notFound": "Ce paiement est introuvable. Rechargez la liste.",
  "payments.refreshFailed": "Le paiement n'a pas pu être actualisé : il est affiché tel qu'il était.",

  "payments.filter.provider": "Moyen de paiement",
  "payments.filter.status": "Statut",
  "payments.filter.all": "Tous",
  "payments.filter.from": "Du",
  "payments.filter.to": "Au",
  "payments.filter.duplicates": "Doublons probables",
  "payments.filter.clearPro": "Tous les pros",
  "payments.list.loading": "Chargement des paiements…",
  "payments.list.loadFailed": "Impossible de charger les paiements.",
  "payments.list.empty": "Aucun paiement pour ces filtres.",
  "payments.list.aria": "Paiements des frais",
  "payments.list.end": "Fin de la liste",
  "payments.pill.duplicate": "Doublon probable",

  "payments.status.pending": "À payer",
  "payments.status.paid": "Payé",
  "payments.status.failed": "Échoué",
  "payments.status.refunded": "Remboursé",
  "payments.status.waived": "Frais annulés",
  "payments.provider.orange_money": "Orange Money",
  "payments.provider.lengo_pay": "Mobile Money",
  "payments.provider.stripe": "Carte (Stripe)",
  "payments.provider.wave": "Wave",
  "payments.feeType.per_job": "Frais de mise en relation",
  "payments.feeType.monthly": "Abonnement mensuel",
  "payments.feeType.yearly": "Abonnement annuel",
  "payments.kind.refund": "Remboursement",
  "payments.kind.credit": "Crédit",
  "payments.kind.waive": "Annulation des frais",
  "payments.kind.mark_paid": "Marqué payé",

  "payments.detail.versionTitle": "La version du paiement affichée : chaque correction l'envoie.",
  "payments.detail.duplicate": "Doublon probable",
  "payments.detail.duplicateBody":
    "Un autre paiement du même pro, du même montant, quelques minutes plus tôt ou plus tard. Vérifiez avant de rembourser :",
  "payments.detail.pro": "Pro",
  "payments.detail.proPayments": "Ses paiements →",
  "payments.detail.transaction": "Référence chez le fournisseur",
  "payments.detail.job": "Demande",
  "payments.detail.refunded": "Déjà remboursé",
  "payments.detail.refundable": "Encore remboursable",
  "payments.detail.credit": "Crédit du pro",
  "payments.detail.adjustments": "Corrections",
  "payments.detail.noAdjustments": "Aucune correction sur ce paiement.",
  "payments.detail.actions": "Corriger",
  "payments.detail.neverOptimistic":
    "Rien n'est affiché comme fait avant la réponse du serveur. Un remboursement sans réponse du fournisseur n'est jamais renvoyé.",
  "payments.col.kind": "Correction",
  "payments.col.amount": "Montant",
  "payments.col.status": "État",
  "payments.col.by": "Par",
  "payments.col.actions": "Actions",

  "payments.adjustment.status.pending": "Statut inconnu, vérification en cours",
  "payments.adjustment.status.done": "Fait",
  "payments.adjustment.status.failed": "Refusé par le fournisseur",
  "payments.adjustment.status.awaiting_manual_transfer": "Virement manuel à faire",
  "payments.adjustment.status.awaiting_approval": "En attente d'accord",
  "payments.adjustment.status.rejected": "Refusé par le propriétaire",
  "payments.adjustment.outcomeUnknownHint":
    "Envoyé au fournisseur sans réponse. Il n'est jamais renvoyé : vérifiez sur son tableau de bord, puis réglez-le.",
  "payments.adjustment.manualHint":
    "Orange Money n'a pas de remboursement automatique : envoyez l'argent à la main, puis réglez avec la référence du transfert.",

  "payments.action.refund": "Rembourser",
  "payments.action.credit": "Créditer",
  "payments.action.waive": "Annuler les frais",
  "payments.action.markPaid": "Marquer payé",
  "payments.action.settle": "Régler",
  "payments.action.approve": "Accorder",
  "payments.action.reject": "Refuser",

  "payments.dialog.refund.title": "Rembourser · {subject}",
  "payments.dialog.refund.body":
    "Le remboursement est enregistré puis envoyé une seule fois au fournisseur. Au-delà du plafond, il attend l'accord d'un propriétaire.",
  "payments.dialog.credit.title": "Créditer · {subject}",
  "payments.dialog.credit.body": "Un solde déduit des prochains frais du pro, dans la même devise.",
  "payments.dialog.waive.title": "Annuler les frais · {subject}",
  "payments.dialog.waive.body":
    "Les frais ne sont plus dus. Au-delà du plafond, l'annulation attend l'accord d'un propriétaire.",
  "payments.dialog.markPaid.title": "Marquer payé · {subject}",
  "payments.dialog.markPaid.body":
    "Seulement avec la référence du vrai paiement (le SMS du pro ou le relevé). Une référence ne marque qu'un seul paiement.",
  "payments.dialog.settle.title": "Régler le remboursement · {subject}",
  "payments.dialog.settle.body": "Notez comment le remboursement s'est terminé, une fois vérifié chez le fournisseur.",
  "payments.dialog.approve.title": "Accorder la demande de {subject}",
  "payments.dialog.approve.body":
    "La correction demandée s'applique dès maintenant. Personne n'accorde sa propre demande.",
  "payments.dialog.reject.title": "Refuser la demande de {subject}",
  "payments.dialog.reject.body": "Rien n'est remboursé ni annulé. Le motif est gardé avec la demande.",
  "payments.field.amount": "Montant ({currency})",
  "payments.field.refundHint": "Laissez vide pour tout rembourser : {amount}.",
  "payments.field.orangeMoneyReference": "Référence de la transaction Orange Money",
  "payments.field.reference": "Référence chez le fournisseur",
  "payments.field.referenceHint": "Copiez-la en entier depuis le SMS ou le relevé : lettres, chiffres et . _ : / -",
  "payments.field.outcome": "Résultat",
  "payments.field.outcome.done": "L'argent est parti",
  "payments.field.outcome.failed": "Le remboursement a échoué",

  "payments.problem.reason": "Donnez un motif d'au moins {min} caractères.",
  "payments.problem.amount": "Saisissez un montant supérieur à zéro, avec au plus deux décimales.",
  "payments.problem.tooMuch": "C'est plus que ce qui reste remboursable.",
  "payments.problem.reference":
    "Copiez la référence en entier : au moins {min} caractères, lettres, chiffres et . _ : / - seulement.",

  "payments.outcome.done": "{kind} de {amount} : fait.",
  "payments.outcome.pending":
    "{kind} de {amount} : statut inconnu, vérification en cours. Il ne sera pas renvoyé ; réglez-le une fois vérifié.",
  "payments.outcome.awaiting_manual_transfer":
    "{kind} de {amount} enregistré : envoyez l'argent à la main, puis réglez-le avec la référence.",
  "payments.outcome.awaiting_approval":
    "{kind} de {amount} : au-delà de ce que vous pouvez faire seul, il attend un accord.",
  "payments.outcome.failed": "{kind} de {amount} refusé par le fournisseur ({failure}). Rien n'a bougé.",
  "payments.outcome.rejected": "{kind} de {amount} refusé.",

  "payments.approvals.status.pending": "En attente",
  "payments.approvals.status.approved": "Accordées",
  "payments.approvals.status.rejected": "Refusées",
  "payments.approvals.loading": "Chargement des demandes…",
  "payments.approvals.loadFailed": "Impossible de charger les demandes d'accord.",
  "payments.approvals.empty": "Aucune demande ici.",
  "payments.approvals.requested": "Demandé par {name} le {date}",
  "payments.approvals.permission": "Permission : {permission}",
  "payments.approvals.theirLimit": "Leur plafond : {amount}",
  "payments.approvals.noPermission": "Cette personne n'a pas cette permission.",
  "payments.approvals.noLimitInCurrency": "Cette personne n'a aucun plafond dans cette devise.",
  "payments.approvals.openPro": "Ouvrir le pro →",
  "payments.approvals.decided": "Tranché par {name} le {date}",
  "payments.approvals.openPayment": "Ouvrir le paiement →",
  "payments.approvals.own": "C'est votre demande : un autre propriétaire doit la trancher.",

  "payments.export.month": "Mois à exporter",
  "payments.export.reason": "Motif (facultatif)",
  "payments.export.button": "Exporter le CSV",
  "payments.export.running": "Export…",
  "payments.export.hint":
    "Les paiements du mois (UTC) pour le rapprochement, sans téléphone ni email. Chaque export est enregistré dans le journal d'audit.",
  "payments.export.done": "Export téléchargé. Il est enregistré dans le journal d'audit.",

  "payments.error.stale":
    "Ce paiement a changé pendant que vous le regardiez. Rechargez-le, vérifiez, puis recommencez.",
  "payments.error.refused":
    "Correction refusée : vos rôles n'incluent pas la fonctionnalité nécessaire. Demandez l'accès à un responsable.",
  "payments.error.approvalRefused":
    "Refusé : seul un propriétaire avec APPROVE_PAYMENT_REQUESTS peut trancher, et jamais sa propre demande.",
  "payments.error.invalid":
    "Refusé : le paiement n'est plus dans l'état voulu, le montant dépasse ce qui reste, ou la référence est déjà utilisée. Rechargez et vérifiez.",
  "payments.error.outcomeUnknown":
    "Pas de réponse du serveur : la correction a peut-être été enregistrée. Réessayez : le même clic ne peut pas être appliqué deux fois.",

  "payments.refused.notRefundable": "Refusé : seuls des frais payés peuvent être remboursés. Rechargez le paiement.",
  "payments.refused.nothingToRefund":
    "Refusé : ces frais sont déjà remboursés en entier, ou le reste est en attente. Rechargez le paiement.",
  "payments.refused.refundTooLarge": "Refusé : le montant dépasse ce qui reste à rembourser sur ces frais.",
  "payments.refused.alreadyAwaitingApproval":
    "Refusé : une exonération de ces frais attend déjà un propriétaire. Rechargez le paiement.",
  "payments.refused.notMarkable":
    "Refusé : seuls des frais en attente ou échoués peuvent être marqués payés. Rechargez le paiement.",
  "payments.refused.referenceInUse":
    "Refusé : cette référence Orange Money a déjà payé d'autres frais. Vérifiez la référence.",
  "payments.refused.referenceRequired": "Saisissez la référence de la transaction.",
  "payments.refused.alreadySettled": "Ce remboursement vient d'être réglé. Rechargez-le.",
  "payments.refused.alreadyDecided": "Cette demande vient d'être tranchée. Rechargez la liste.",
  "payments.refused.ownRequest": "Refusé : vous avez fait cette demande, un autre propriétaire doit la trancher.",
  "payments.refused.approverNotPermitted":
    "Vous n'avez pas cette permission pour ce montant : vous ne pouvez pas trancher. Quelqu'un d'autre tranche.",
  "payments.refused.paymentChanged":
    "Refusé : le paiement a changé depuis la demande. Rejetez-la plutôt, avec le motif.",
  "payments.refused.currencyMismatch": "Refusé : le crédit doit être dans la devise des frais.",
  "payments.refused.feeOfAnotherPro": "Refusé : ces frais ont été payés par un autre pro.",
  "payments.refused.invalidAmount": "Refusé : le montant doit être supérieur à zéro.",
} as const;

export type PaymentsMessageId = keyof typeof paymentsFr;
